import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  CircularProgress,
    Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const ReportSubmission = () => {
  // State for all forms
  const [pretherapy, setPretherapy] = useState({
    opNumber: '',
    caseName: '',
    studentClinician: '',
    supervisor: '',
    ageGender: '',
    languages: [], // Changed to array for multi-select
    device: '',
    reportFile: null,
    email: ''
  });

    const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [downloadFormat, setDownloadFormat] = useState('excel');
  const [lessonPlan, setLessonPlan] = useState({
    opNumber: '',
    caseName: '',
    ageGender: '',
    device: '',
    supervisorName: '',
    reportFile: null,
    email: ''
  });

  const [progressReport, setProgressReport] = useState({
    opNumber: '',
    caseName: '',
    ageGender: '',
    device: '',
    supervisorName: '',
    reportFile: null,
    email: ''
  });

  // Loading and alert states
  const [loading, setLoading] = useState({
    pretherapy: false,
    lessonPlan: false,
    progressReport: false
  });
  
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const languageOptions = ['Kannada', 'Hindi', 'Malayalam', 'Telugu', 'Tamil', 'English', 'Marathi', 'Bengali'];
  const deviceOptions = ['Hearing aid', 'CI', 'Hearing aid + CI', 'Bilateral CI'];

  // Utility functions
  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'success' }), 5000);
  };

  const resetForm = (formType) => {
    const emptyState = {
      opNumber: '',
      caseName: '',
      studentClinician: '',
      supervisor: '',
      ageGender: '',
      languages: [],
      device: '',
      reportFile: null,
      supervisorName: '',
      email: ''
    };

    switch (formType) {
      case 'pretherapy':
        setPretherapy(emptyState);
        break;
      case 'lessonPlan':
        setLessonPlan(emptyState);
        break;
      case 'progressReport':
        setProgressReport(emptyState);
        break;
    }
  };

  // Multi-select render value
  const renderMultiSelectValue = (selected) => {
    if (selected.length === 0) {
      return <em>Select Languages</em>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    );
  };

  // Pretherapy Handlers
  const handlePretherapyChange = (field, value) => {
    setPretherapy(prev => ({ ...prev, [field]: value }));
  };

  const handlePretherapySubmit = async () => {
    // Validation
    if (!pretherapy.opNumber || !pretherapy.caseName || !pretherapy.studentClinician || 
        !pretherapy.supervisor || !pretherapy.ageGender || !pretherapy.device || 
        !pretherapy.email || !pretherapy.reportFile) {
      showAlert('Please fill all required fields.', 'warning');
      return;
    }

    if (pretherapy.languages.length === 0) {
      showAlert('Please select at least one language.', 'warning');
      return;
    }

    setLoading(prev => ({ ...prev, pretherapy: true }));

    try {
      const formData = new FormData();
      formData.append('op_number', pretherapy.opNumber);
      formData.append('case_name', pretherapy.caseName);
      formData.append('student_clinician', pretherapy.studentClinician);
      formData.append('supervisor', pretherapy.supervisor);
      formData.append('age_gender', pretherapy.ageGender);
      formData.append('languages', JSON.stringify(pretherapy.languages));
      formData.append('device', pretherapy.device);
      formData.append('email', pretherapy.email);
      formData.append('report_file', pretherapy.reportFile);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/pretherapy`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        showAlert(result.message, 'success');
        resetForm('pretherapy');
      } else {
        throw new Error(result.message || 'Failed to submit pretherapy report');
      }
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(prev => ({ ...prev, pretherapy: false }));
    }
  };

  // Lesson Plan Handlers
  const handleLessonPlanChange = (field, value) => {
    setLessonPlan(prev => ({ ...prev, [field]: value }));
  };

  const handleLessonPlanSubmit = async () => {
    // Validation
    if (!lessonPlan.opNumber || !lessonPlan.caseName || !lessonPlan.ageGender || 
        !lessonPlan.device || !lessonPlan.supervisorName || !lessonPlan.email || 
        !lessonPlan.reportFile) {
      showAlert('Please fill all required fields.', 'warning');
      return;
    }

    setLoading(prev => ({ ...prev, lessonPlan: true }));

    try {
      const formData = new FormData();
      formData.append('op_number', lessonPlan.opNumber);
      formData.append('case_name', lessonPlan.caseName);
      formData.append('age_gender', lessonPlan.ageGender);
      formData.append('device', lessonPlan.device);
      formData.append('supervisor_name', lessonPlan.supervisorName);
      formData.append('email', lessonPlan.email);
      formData.append('report_file', lessonPlan.reportFile);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/lesson-plan`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        showAlert(result.message, 'success');
        resetForm('lessonPlan');
      } else {
        throw new Error(result.message || 'Failed to submit lesson plan');
      }
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(prev => ({ ...prev, lessonPlan: false }));
    }
  };

  // Progress Report Handlers
  const handleProgressReportChange = (field, value) => {
    setProgressReport(prev => ({ ...prev, [field]: value }));
  };

  const handleProgressReportSubmit = async () => {
    // Validation
    if (!progressReport.opNumber || !progressReport.caseName || !progressReport.ageGender || 
        !progressReport.device || !progressReport.supervisorName || !progressReport.email || 
        !progressReport.reportFile) {
      showAlert('Please fill all required fields.', 'warning');
      return;
    }

    setLoading(prev => ({ ...prev, progressReport: true }));

    try {
      const formData = new FormData();
      formData.append('op_number', progressReport.opNumber);
      formData.append('case_name', progressReport.caseName);
      formData.append('age_gender', progressReport.ageGender);
      formData.append('device', progressReport.device);
      formData.append('supervisor_name', progressReport.supervisorName);
      formData.append('email', progressReport.email);
      formData.append('report_file', progressReport.reportFile);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/progress-report`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        showAlert(result.message, 'success');
        resetForm('progressReport');
      } else {
        throw new Error(result.message || 'Failed to submit progress report');
      }
    } catch (error) {
      showAlert(error.message, 'error');
    } finally {
      setLoading(prev => ({ ...prev, progressReport: false }));
    }
  };
 const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Supervisor Lesson Plan ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "supervisor_lesson_plans",
          formName: "Supervisor Lesson Plan",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Supervisor_Lesson_Plan_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };
  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
       <Box sx={{ position: 'absolute', top: 300, right: 140 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
        Report Submission (Clinician)
      </Typography>

      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Pretherapy Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Pretherapy Report
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="IP/OP/Internal No"
              value={pretherapy.opNumber}
              onChange={(e) => handlePretherapyChange('opNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Case Name"
              value={pretherapy.caseName}
              onChange={(e) => handlePretherapyChange('caseName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Student Clinician"
              value={pretherapy.studentClinician}
              onChange={(e) => handlePretherapyChange('studentClinician', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Supervisor"
              value={pretherapy.supervisor}
              onChange={(e) => handlePretherapyChange('supervisor', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Age/Gender"
              value={pretherapy.ageGender}
              onChange={(e) => handlePretherapyChange('ageGender', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Languages</InputLabel>
              <Select
                multiple
                value={pretherapy.languages}
                onChange={(e) => handlePretherapyChange('languages', e.target.value)}
                input={<OutlinedInput label="Languages" />}
                renderValue={renderMultiSelectValue}
                displayEmpty
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Device</InputLabel>
              <Select
                value={pretherapy.device}
                onChange={(e) => handlePretherapyChange('device', e.target.value)}
                label="Device"
                displayEmpty
                renderValue={value => value || 'Select Device'}
              >
                {deviceOptions.map((device) => (
                  <MenuItem key={device} value={device}>{device}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={pretherapy.email}
              onChange={(e) => handlePretherapyChange('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" component="label" fullWidth sx={{ py: 1.5 }}>
              {pretherapy.reportFile ? `Report Selected: ${pretherapy.reportFile.name}` : 'Upload Pretherapy Report'}
              <input
                type="file"
                hidden
                onChange={(e) => handlePretherapyChange('reportFile', e.target.files[0])}
              />
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePretherapySubmit}
              disabled={loading.pretherapy}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {loading.pretherapy ? <CircularProgress size={24} /> : 'Submit Pretherapy Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Lesson Plan Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Lesson Plan
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="IP/OP/Internal No"
              value={lessonPlan.opNumber}
              onChange={(e) => handleLessonPlanChange('opNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Case Name"
              value={lessonPlan.caseName}
              onChange={(e) => handleLessonPlanChange('caseName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Age/Gender"
              value={lessonPlan.ageGender}
              onChange={(e) => handleLessonPlanChange('ageGender', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Device</InputLabel>
              <Select
                value={lessonPlan.device}
                onChange={(e) => handleLessonPlanChange('device', e.target.value)}
                label="Device"
                displayEmpty
                renderValue={value => value || 'Select Device'}
              >
                {deviceOptions.map((device) => (
                  <MenuItem key={device} value={device}>{device}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Supervisor Name"
              value={lessonPlan.supervisorName}
              onChange={(e) => handleLessonPlanChange('supervisorName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={lessonPlan.email}
              onChange={(e) => handleLessonPlanChange('email', e.target.value)}
            />
          </Grid>
<Grid item xs={12}>
  <Button 
    variant="outlined" 
    component="label" 
    fullWidth 
    startIcon={<CloudUploadIcon />}
    sx={{ 
      py: 1.5, 
      borderStyle: 'dashed',
      borderColor: pretherapy.reportFile ? 'success.main' : 'primary.main'
    }}
  >
    {pretherapy.reportFile ? 
      `Report Selected: ${pretherapy.reportFile.name} (${(pretherapy.reportFile.size / 1024 / 1024).toFixed(2)} MB)` : 
      'Upload Pretherapy Report'
    }
    <input
      type="file"
      hidden
      onChange={(e) => handlePretherapyChange('reportFile', e.target.files[0])}
    />
  </Button>
  {pretherapy.reportFile && (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
      File will be attached to the confirmation email
    </Typography>
  )}
</Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleLessonPlanSubmit}
              disabled={loading.lessonPlan}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {loading.lessonPlan ? <CircularProgress size={24} /> : 'Submit Lesson Plan'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Progress Report Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Progress Report
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="OP Number"
              value={progressReport.opNumber}
              onChange={(e) => handleProgressReportChange('opNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Case Name"
              value={progressReport.caseName}
              onChange={(e) => handleProgressReportChange('caseName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Age/Gender"
              value={progressReport.ageGender}
              onChange={(e) => handleProgressReportChange('ageGender', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Device</InputLabel>
              <Select
                value={progressReport.device}
                onChange={(e) => handleProgressReportChange('device', e.target.value)}
                label="Device"
                displayEmpty
                renderValue={value => value || 'Select Device'}
               
              >
                {deviceOptions.map((device) => (
                  <MenuItem key={device} value={device}>{device}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Supervisor Name"
              value={progressReport.supervisorName}
              onChange={(e) => handleProgressReportChange('supervisorName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={progressReport.email}
              onChange={(e) => handleProgressReportChange('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" component="label" fullWidth sx={{ py: 1.5 }}>
              {progressReport.reportFile ? `Progress Report Selected: ${progressReport.reportFile.name}` : 'Upload Progress Report'}
              <input
                type="file"
                hidden
                onChange={(e) => handleProgressReportChange('reportFile', e.target.files[0])}
              />
            </Button>
          </Grid>
                   <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProgressReportSubmit}
              disabled={loading.progressReport}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {loading.progressReport ? <CircularProgress size={24} /> : 'Submit Progress Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ReportSubmission;